import * as SelectedItemActions from './selected-item.actions'

import { Injectable } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { switchMap, catchError, tap, withLatestFrom, map } from 'rxjs'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { SelectedItemApiService } from './selected-item-api.service'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { SelectedItemFacade } from './selected-item.facade'
import { HttpErrorResponse } from '@angular/common/http'
import { GbDialogData } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/message-data.models'
import { TranslateService } from '@ngx-translate/core'
import { GbMessageDialogService } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/gb-message-dialog.service'
import { GbDisplayNamePipe } from 'libs/shared/utils/pipes/src/lib/gb-display-name-pipe/gb-display-name.pipe'
import { GbPaymentService } from 'libs/gb-front/payment/util/src/lib/gb-payment.service'
import { DonationPayload } from './selected-item.models'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'

@Injectable()
export class SelectedItemDonationEffects {
  placeDonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.placeDonation),
      withLatestFrom(
        this.authFacade.authentication$,
        this.selectedProfileFacade.selectedProfileId$,
        this.selectedItemFacade.selectedItem$,
        this.auctionFacade.auctionUrlName$,
        this.selectedItemFacade.currentAmount$,
        this.auctionFacade.selectedPaymentMethod$,
        this.gbPaymentService.tippingFeeAmount$,
        this.gbPaymentService.applyPaymentMethodFee$,
      ),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.PlaceDonation)),
      switchMap(
        ([
          action,
          authentication,
          selectedProfileId,
          item,
          urlName,
          amount,
          selectedPaymentMethod,
          tippingFeeAmount,
          applyPaymentMethodFee,
        ]) => {
          const createDonationPayload: DonationPayload = {
            amount: amount,
            donation_item: item?.donation_item?.uuid,
            profile: selectedProfileId,
            delegate: authentication.profile.profile,
          }

          if (item?.donation_item?.pay_now) {
            createDonationPayload.payment_method = selectedPaymentMethod?.uuid
            createDonationPayload.apply_fee = applyPaymentMethodFee
          }

          if (tippingFeeAmount) {
            createDonationPayload.tipping_fee = tippingFeeAmount
          }

          return this.selectedItemApiService.createDonation(urlName, createDonationPayload).pipe(
            map((response) => {
              const payload = {
                requiresPayment: response.payment_pending && item?.donation_item?.pay_now,
                payment: response.payment,
              }

              return SelectedItemActions.placeDonationSuccess(payload)
            }),
            catchError(async (error: HttpErrorResponse) => SelectedItemActions.placeDonationFailure({ error })),
          )
        },
      ),
    ),
  )

  showDonationSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.finaliseDonationSuccess),
        withLatestFrom(
          this.authFacade.isVolunteer$,
          this.selectedProfileFacade.selectedProfile$,
          this.selectedItemFacade.selectedItem$,
        ),
        map(([action, isVolunteer, selectedProfile, item]) => {
          let dialogPayload: GbDialogData = {
            title: this.translateService.instant('ITEM.DONATE.SUCCESS_MESSAGE_HEADER'),
            body: this.translateService.instant('ITEM.DONATE.SUCCESS_MESSAGE_DESCRIPTION'),
            activityFeed: true,
            messageType: 'success',
            item: item,
          }

          if (isVolunteer) {
            dialogPayload = {
              title: 'ITEM.DONATE.SUCCESS_MESSAGE_HEADER_VOLUNTEER',
              body: this.translateService.instant('ITEM.DONATE.SUCCESS_MESSAGE_DESCRIPTION_VOLUNTEER', {
                user: this.gbDisplayNamePipe.transform(selectedProfile),
              }),
              messageType: 'success',
              item: item,
            }
          }
          this.gbMessageDialogService.showDialog(dialogPayload)
        }),
      ),
    { dispatch: false },
  )

  placeDonationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.placeDonationFailure),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          this.gbMessageDialogService.showDialog({
            title: 'ITEM.DONATE.ERROR_MESSAGE.TITLE',
            body: error || 'ITEM.DONATE.ERROR_MESSAGE.BODY',
            messageType: 'warning',
          })
        }),
      ),
    { dispatch: false },
  )

  hideDonationLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.placeDonationSuccess, SelectedItemActions.placeDonationFailure),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.PlaceDonation)),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private selectedItemApiService: SelectedItemApiService,
    private selectedProfileFacade: SelectedProfileFacade,
    private selectedItemFacade: SelectedItemFacade,
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private loaderService: GbLoaderService,
    private gbPaymentService: GbPaymentService,
    private gbMessageDialogService: GbMessageDialogService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbDisplayNamePipe: GbDisplayNamePipe,
    private translateService: TranslateService,
  ) {}
}
